/**
 * Free particle emitters (audit 5.4 — the half fragments don't cover):
 * confetti bursts and sparkles that are drawn AROUND an element rather than
 * cut from its image. Closed-form only — every particle's state is a pure
 * function of (progress, seed): position = origin + v·t + ½g·t², so export
 * seeking reproduces playback exactly and there is no simulation state.
 */

export const EMIT_KINDS = { confetti: 0, sparkle: 1 } as const;

export type EmitterConfig = {
  /** 0..1, tweened */
  progress: number;
  /** EMIT_KINDS value */
  kind: number;
  /** particle count, clamped 4..80 */
  count: number;
  /** sideways launch strength (confetti) / halo width (sparkle) */
  spread: number;
  /** downward pull (confetti) */
  gravity: number;
  /** particle size multiplier */
  size: number;
};

export type EmitterParticle = {
  /** relative to the emitter rect's origin */
  x: number;
  y: number;
  size: number;
  /** radians */
  rotate: number;
  /** 0..1 */
  alpha: number;
  colorIndex: number;
};

/** Confetti palette — visual constants, like the track-channel colors. */
export const CONFETTI_COLORS: readonly string[] = [
  "#ff595e",
  "#ffca3a",
  "#8ac926",
  "#1982c4",
  "#6a4c93",
];

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Deterministic pseudo-random in [0, 1) from two integers. */
const prand = (a: number, b: number) => {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
};

/** All particles for one frame of an emitter over a w×h rect. */
export const getEmitterParticles = (
  cfg: EmitterConfig,
  seed: number,
  w: number,
  h: number,
): EmitterParticle[] => {
  const count = Math.max(4, Math.min(80, Math.round(cfg.count)));
  const p = clamp01(cfg.progress);
  const particles: EmitterParticle[] = [];
  const baseSize = Math.min(w, h) * 0.045 * cfg.size;

  for (let idx = 0; idx < count; idx++) {
    const r = (k: number) => prand(seed + idx, k);
    if (cfg.kind === EMIT_KINDS.sparkle) {
      // twinkling diamonds in a halo around the rect; alpha cycles with a
      // per-particle phase so loops are seamless and bounded
      const halo = 0.15 * cfg.spread;
      particles.push({
        x: w * (r(1) * (1 + halo * 2) - halo),
        y: h * (r(2) * (1 + halo * 2) - halo),
        size: baseSize * (0.4 + r(3) * 0.8),
        rotate: r(4) * (Math.PI / 2) + p * 2,
        alpha: Math.abs(Math.sin(Math.PI * (p * 2 + r(5)))),
        colorIndex: idx % CONFETTI_COLORS.length,
      });
      continue;
    }
    // confetti: staggered birth, ballistic arc, spin, fade-out by p=1
    const birth = r(0) * 0.2;
    const t = Math.max(0, p - birth);
    if (t <= 0) {
      continue;
    }
    const x0 = w * (0.3 + r(1) * 0.4);
    const y0 = h * 0.1;
    const vx = (r(2) - 0.5) * 2 * cfg.spread * w * 1.2;
    const vy = -(0.8 + r(3) * 0.8) * h * 2;
    particles.push({
      x: x0 + vx * t,
      y: y0 + vy * t + 0.5 * cfg.gravity * h * 8 * t * t,
      size: baseSize * (0.5 + r(6)),
      rotate: r(4) * Math.PI * 2 + t * (r(5) - 0.5) * 12,
      alpha: clamp01((1 - p) / 0.3),
      colorIndex: idx % CONFETTI_COLORS.length,
    });
  }
  return particles;
};
